import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Loader2, Lock, UserPlus, AlertTriangle, Image as ImageIcon, Globe, TrendingUp, Palette, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [savingSite, setSavingSite] = useState(false);
  const [savingPages, setSavingPages] = useState(false);
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [newAdminData, setNewAdminData] = useState({
    username: '',
    email: '',
    password: ''
  });

  const [siteSettings, setSiteSettings] = useState({
    logo_url: '',
    logo_dark_url: '',
    favicon_url: '',
    og_image_url: '',
    meta_title: '',
    meta_description: '',
    facebook_pixel_id: '',
    google_analytics_id: '',
    primary_color: '#2B2B2B',
    secondary_color: '#A8B39C',
    button_color: '#336DBF',
    loomy_crm_api_key: '',
    loomy_crm_enabled: false
  });

  const [pageSEO, setPageSEO] = useState([]);

  useEffect(() => {
    checkSessionAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkSessionAndLoad = async () => {
    try {
      // Check if user is authenticated via cookie
      await api.get(`${API_BASE}/admin/me`, { withCredentials: true });
      // If successful, load data
      loadSiteSettings();
      loadPageSEO();
    } catch (error) {
      console.error('Session check failed:', error);
      navigate('/admin/login');
    }
  };

  const loadSiteSettings = async () => {
    try {
      const response = await api.get(`${API_BASE}/admin/site-settings`, {
        withCredentials: true
      });
      if (response.data) {
        // Ensure all string fields have default empty string instead of null
        const data = response.data;
        setSiteSettings({
          logo_url: data.logo_url || '',
          logo_dark_url: data.logo_dark_url || '',
          favicon_url: data.favicon_url || '',
          og_image_url: data.og_image_url || '',
          meta_title: data.meta_title || '',
          meta_description: data.meta_description || '',
          facebook_pixel_id: data.facebook_pixel_id || '',
          google_analytics_id: data.google_analytics_id || '',
          primary_color: data.primary_color || '#2B2B2B',
          secondary_color: data.secondary_color || '#A8B39C',
          button_color: data.button_color || '#336DBF',
          loomy_crm_api_key: data.loomy_crm_api_key || '',
          loomy_crm_enabled: data.loomy_crm_enabled || false
        });
      }
    } catch (error) {
      console.error('Error loading site settings:', error);
    }
  };

  const loadPageSEO = async () => {
    try {
      const response = await api.get(`${API_BASE}/admin/page-seo`, {
        withCredentials: true
      });
      if (response.data) {
        setPageSEO(response.data);
      }
    } catch (error) {
      console.error('Error loading page SEO:', error);
    }
  };

  const handleSaveSiteSettings = async (e) => {
    e.preventDefault();
    setSavingSite(true);

    try {
      await api.put(
        `${API_BASE}/admin/site-settings`,
        siteSettings,
        { withCredentials: true }
      );

      toast.success('Paramètres enregistrés');
    } catch (error) {
      console.error('Error saving site settings:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSavingSite(false);
    }
  };

  const handleSavePageSEO = async (pageUrl) => {
    setSavingPages(true);

    try {
      const pageData = pageSEO.find(p => p.page_url === pageUrl);
      if (!pageData) return;

      await api.put(
        `${API_BASE}/admin/page-seo/${encodeURIComponent(pageUrl)}`,
        pageData,
        { withCredentials: true }
      );

      toast.success('SEO de la page enregistré');
    } catch (error) {
      console.error('Error saving page SEO:', error);
      toast.error('Erreur');
    } finally {
      setSavingPages(false);
    }
  };

  const updatePageSEO = (pageUrl, field, value) => {
    setPageSEO(prev => prev.map(p => 
      p.page_url === pageUrl ? { ...p, [field]: value } : p
    ));
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordData.new_password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setLoading(true);

    try {
      await api.post(
        `${API_BASE}/admin/change-password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        },
        { withCredentials: true }
      );

      toast.success('Mot de passe changé avec succès');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Erreur', {
        description: error.response?.data?.detail || 'Vérifiez votre mot de passe actuel.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();

    if (newAdminData.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setLoading(true);

    try {
      await api.post(
        `${API_BASE}/admin/create`,
        newAdminData,
        { withCredentials: true }
      );

      toast.success('Nouvel administrateur créé');
      setNewAdminData({ username: '', email: '', password: '' });
    } catch (error) {
      console.error('Error creating admin:', error);
      toast.error('Erreur', {
        description: error.response?.data?.detail || 'Nom d\'utilisateur ou email déjà utilisé.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/admin/leads')}
        className="mb-4"
        data-testid="back-to-leads-button"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Retour aux leads
      </Button>

      <Card className="border-orange-200 bg-orange-50">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            <CardTitle className="text-orange-900">Sécurité Important</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-orange-800">
            <strong>Action requise :</strong> Si vous utilisez encore les identifiants par défaut 
            (admin / admin123), veuillez <strong>impérativement</strong> changer votre mot de passe 
            immédiatement via l'onglet "Sécurité".
          </p>
        </CardContent>
      </Card>

      <Tabs defaultValue="branding" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="branding">Branding</TabsTrigger>
          <TabsTrigger value="appearance">Apparence</TabsTrigger>
          <TabsTrigger value="seo">SEO Pages</TabsTrigger>
          <TabsTrigger value="tracking">Tracking</TabsTrigger>
          <TabsTrigger value="security">Sécurité</TabsTrigger>
        </TabsList>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                <CardTitle>Logos & Images</CardTitle>
              </div>
              <CardDescription>Logos, favicon et image de partage réseaux sociaux</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveSiteSettings} className="space-y-4">
                <div>
                  <Label htmlFor="logo_url">URL Logo (fond clair)</Label>
                  <Input
                    id="logo_url"
                    value={siteSettings.logo_url}
                    onChange={(e) => setSiteSettings({ ...siteSettings, logo_url: e.target.value })}
                    placeholder="https://exemple.com/logo.png"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Logo visible en mode clair</p>
                </div>

                <div>
                  <Label htmlFor="logo_dark_url">URL Logo (fond sombre)</Label>
                  <Input
                    id="logo_dark_url"
                    value={siteSettings.logo_dark_url}
                    onChange={(e) => setSiteSettings({ ...siteSettings, logo_dark_url: e.target.value })}
                    placeholder="https://exemple.com/logo-dark.png"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Logo visible en mode sombre</p>
                </div>

                <div>
                  <Label htmlFor="favicon_url">URL Favicon</Label>
                  <Input
                    id="favicon_url"
                    value={siteSettings.favicon_url}
                    onChange={(e) => setSiteSettings({ ...siteSettings, favicon_url: e.target.value })}
                    placeholder="https://exemple.com/favicon.ico"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Favicon du site (32x32 ou 64x64px)</p>
                </div>

                <div>
                  <Label htmlFor="og_image_url">URL Image OG (Réseaux sociaux)</Label>
                  <Input
                    id="og_image_url"
                    value={siteSettings.og_image_url}
                    onChange={(e) => setSiteSettings({ ...siteSettings, og_image_url: e.target.value })}
                    placeholder="https://exemple.com/og-image.jpg"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Image affichée lors du partage (1200x630px recommandé)</p>
                </div>

                <Separator />

                <div>
                  <Label htmlFor="meta_title">Meta Title Global</Label>
                  <Input
                    id="meta_title"
                    value={siteSettings.meta_title}
                    onChange={(e) => setSiteSettings({ ...siteSettings, meta_title: e.target.value })}
                    placeholder="Titre du site"
                    maxLength={60}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Titre par défaut (max 60 caractères)</p>
                </div>

                <div>
                  <Label htmlFor="meta_description">Meta Description Globale</Label>
                  <Textarea
                    id="meta_description"
                    value={siteSettings.meta_description}
                    onChange={(e) => setSiteSettings({ ...siteSettings, meta_description: e.target.value })}
                    placeholder="Description du site"
                    rows={3}
                    maxLength={160}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Description par défaut (max 160 caractères)</p>
                </div>

                <Button
                  type="submit"
                  disabled={savingSite}
                  style={{ backgroundColor: 'var(--cta-blue)' }}
                >
                  {savingSite && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Enregistrer
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Appearance Tab */}
        <TabsContent value="appearance">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                <CardTitle>Couleurs du site</CardTitle>
              </div>
              <CardDescription>Personnalisez les couleurs primaires, secondaires et des boutons</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveSiteSettings} className="space-y-6">
                <div>
                  <Label htmlFor="primary_color">Couleur Primaire</Label>
                  <div className="flex gap-4 items-center mt-2">
                    <Input
                      id="primary_color"
                      type="color"
                      value={siteSettings.primary_color}
                      onChange={(e) => setSiteSettings({ ...siteSettings, primary_color: e.target.value })}
                      className="w-20 h-10"
                    />
                    <Input
                      value={siteSettings.primary_color}
                      onChange={(e) => setSiteSettings({ ...siteSettings, primary_color: e.target.value })}
                      placeholder="#2B2B2B"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Couleur principale du site (par défaut: Anthracite #2B2B2B)</p>
                </div>

                <div>
                  <Label htmlFor="secondary_color">Couleur Secondaire</Label>
                  <div className="flex gap-4 items-center mt-2">
                    <Input
                      id="secondary_color"
                      type="color"
                      value={siteSettings.secondary_color}
                      onChange={(e) => setSiteSettings({ ...siteSettings, secondary_color: e.target.value })}
                      className="w-20 h-10"
                    />
                    <Input
                      value={siteSettings.secondary_color}
                      onChange={(e) => setSiteSettings({ ...siteSettings, secondary_color: e.target.value })}
                      placeholder="#A8B39C"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Couleur secondaire/accent (par défaut: Sage #A8B39C)</p>
                </div>

                <div>
                  <Label htmlFor="button_color">Couleur des Boutons CTA</Label>
                  <div className="flex gap-4 items-center mt-2">
                    <Input
                      id="button_color"
                      type="color"
                      value={siteSettings.button_color}
                      onChange={(e) => setSiteSettings({ ...siteSettings, button_color: e.target.value })}
                      className="w-20 h-10"
                    />
                    <Input
                      value={siteSettings.button_color}
                      onChange={(e) => setSiteSettings({ ...siteSettings, button_color: e.target.value })}
                      placeholder="#336DBF"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Couleur des boutons d'action (par défaut: Bleu #336DBF)</p>
                </div>

                <Separator />

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setSiteSettings({
                      ...siteSettings,
                      primary_color: '#2B2B2B',
                      secondary_color: '#A8B39C',
                      button_color: '#336DBF'
                    })}
                  >
                    Réinitialiser couleurs par défaut
                  </Button>
                  <Button
                    type="submit"
                    disabled={savingSite}
                    style={{ backgroundColor: 'var(--cta-blue)' }}
                  >
                    {savingSite && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Enregistrer
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SEO Pages Tab */}
        <TabsContent value="seo">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                <CardTitle>SEO par Page</CardTitle>
              </div>
              <CardDescription>Meta Title et Description pour chaque page (optimisé pour Google)</CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {pageSEO.map((page, idx) => (
                  <AccordionItem key={idx} value={`page-${idx}`}>
                    <AccordionTrigger>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">{page.page_url}</span>
                        <span className="text-xs text-muted-foreground">({page.meta_title.substring(0, 40)}...)</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-4 pt-4">
                        <div>
                          <Label>Meta Title</Label>
                          <Input
                            value={page.meta_title}
                            onChange={(e) => updatePageSEO(page.page_url, 'meta_title', e.target.value)}
                            maxLength={60}
                          />
                          <p className="text-xs text-muted-foreground mt-1">{page.meta_title.length}/60 caractères</p>
                        </div>
                        <div>
                          <Label>Meta Description</Label>
                          <Textarea
                            value={page.meta_description}
                            onChange={(e) => updatePageSEO(page.page_url, 'meta_description', e.target.value)}
                            rows={3}
                            maxLength={160}
                          />
                          <p className="text-xs text-muted-foreground mt-1">{page.meta_description.length}/160 caractères</p>
                        </div>
                        <Button
                          onClick={() => handleSavePageSEO(page.page_url)}
                          disabled={savingPages}
                          size="sm"
                        >
                          {savingPages && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Enregistrer cette page
                        </Button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tracking Tab */}
        <TabsContent value="tracking">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                <CardTitle>Tracking & Analytics</CardTitle>
              </div>
              <CardDescription>Facebook Pixel et Google Analytics</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveSiteSettings} className="space-y-4">
                <div>
                  <Label htmlFor="facebook_pixel_id">Facebook Pixel ID</Label>
                  <Input
                    id="facebook_pixel_id"
                    value={siteSettings.facebook_pixel_id}
                    onChange={(e) => setSiteSettings({ ...siteSettings, facebook_pixel_id: e.target.value })}
                    placeholder="123456789012345"
                  />
                  <p className="text-xs text-muted-foreground mt-1">ID de votre pixel Facebook (nombre à 15 chiffres)</p>
                </div>

                <div>
                  <Label htmlFor="google_analytics_id">Google Analytics ID (GA4)</Label>
                  <Input
                    id="google_analytics_id"
                    value={siteSettings.google_analytics_id}
                    onChange={(e) => setSiteSettings({ ...siteSettings, google_analytics_id: e.target.value })}
                    placeholder="G-XXXXXXXXXX"
                  />
                  <p className="text-xs text-muted-foreground mt-1">ID de mesure Google Analytics 4 (format G-XXXXXXXXXX)</p>
                </div>

                <Separator className="my-6" />

                {/* Loomy CRM Integration */}
                <div className="space-y-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                    <h3 className="font-semibold text-blue-900">Intégration Loomy CRM</h3>
                  </div>
                  <p className="text-sm text-blue-800">
                    Envoyez automatiquement tous les leads vers Loomy CRM. Pulsy appellera chaque lead ! 🚀
                  </p>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="loomy_crm_enabled"
                      checked={siteSettings.loomy_crm_enabled}
                      onChange={(e) => setSiteSettings({ ...siteSettings, loomy_crm_enabled: e.target.checked })}
                      className="h-4 w-4"
                    />
                    <Label htmlFor="loomy_crm_enabled" className="cursor-pointer">
                      Activer l'intégration Loomy CRM
                    </Label>
                  </div>

                  <div>
                    <Label htmlFor="loomy_crm_api_key">Clé API de marque Loomy CRM *</Label>
                    <Input
                      id="loomy_crm_api_key"
                      value={siteSettings.loomy_crm_api_key}
                      onChange={(e) => setSiteSettings({ ...siteSettings, loomy_crm_api_key: e.target.value })}
                      placeholder="brand_xxxxxxxxxxxxx"
                      disabled={!siteSettings.loomy_crm_enabled}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Trouvez votre clé dans Loomy CRM : <strong>Réglages → Marques Commerciales</strong>
                    </p>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={savingSite}
                  style={{ backgroundColor: 'var(--cta-blue)' }}
                >
                  {savingSite && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Enregistrer
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                <CardTitle>Changer le mot de passe</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <Label htmlFor="current">Mot de passe actuel *</Label>
                  <Input
                    id="current"
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="new">Nouveau mot de passe * (min 8 caractères)</Label>
                  <Input
                    id="new"
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    required
                    minLength={8}
                  />
                </div>
                <div>
                  <Label htmlFor="confirm">Confirmer le mot de passe *</Label>
                  <Input
                    id="confirm"
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  style={{ backgroundColor: 'var(--cta-blue)' }}
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Changer le mot de passe
                </Button>
              </form>
            </CardContent>
          </Card>

          <Separator />

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                <CardTitle>Créer un nouvel administrateur</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateAdmin} className="space-y-4">
                <div>
                  <Label htmlFor="new-username">Nom d'utilisateur * (min 3 caractères)</Label>
                  <Input
                    id="new-username"
                    value={newAdminData.username}
                    onChange={(e) => setNewAdminData({ ...newAdminData, username: e.target.value })}
                    required
                    minLength={3}
                  />
                </div>
                <div>
                  <Label htmlFor="new-email">Email *</Label>
                  <Input
                    id="new-email"
                    type="email"
                    value={newAdminData.email}
                    onChange={(e) => setNewAdminData({ ...newAdminData, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="new-admin-password">Mot de passe * (min 8 caractères)</Label>
                  <Input
                    id="new-admin-password"
                    type="password"
                    value={newAdminData.password}
                    onChange={(e) => setNewAdminData({ ...newAdminData, password: e.target.value })}
                    required
                    minLength={8}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  variant="secondary"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Créer l'administrateur
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
